import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  Calculator,
  Plus,
  Search,
  Sparkles,
  Download,
  Trash2,
  Eye,
  Pencil,
  CheckCircle,
  AlertTriangle,
  FileText,
} from 'lucide-react';
import { api } from '../../services/api';
import { useSettings } from '../../hooks/useSettings.jsx';
import { useAuth } from '../../hooks/useAuth.jsx';
import { formatCurrency, formatNumber, sanitizeForm } from '../../utils/helpers';
import { USER_ROLES } from '../../utils/constants';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';
import Modal from '../Common/Modal';
import toast from 'react-hot-toast';

const ENTITY_TYPES = [
  { value: 'request', labelKey: 'estimates.attachRequest' },
  { value: 'project', labelKey: 'estimates.attachProject' },
  { value: 'task', labelKey: 'estimates.attachTask' },
  { value: 'time_entry', labelKey: 'estimates.attachTimeEntry' },
];

const STATUS_LABEL_KEYS = {
  draft: 'estimates.statusDraft',
  estimated: 'estimates.statusEstimated',
  shared: 'estimates.statusShared',
  archived: 'estimates.statusArchived',
};

const ENTITY_TYPE_LABEL_KEYS = {
  request: 'estimates.typeRequest',
  project: 'estimates.typeProject',
  task: 'estimates.typeTask',
  time_entry: 'estimates.typeTimeEntry',
};

const CONFIDENCE_LABEL_KEYS = {
  low: 'estimates.confidenceLow',
  medium: 'estimates.confidenceMedium',
  high: 'estimates.confidenceHigh',
};

const STATUS_BADGE = {
  draft: 'bg-gray-100 text-gray-800',
  estimated: 'bg-blue-100 text-blue-800',
  shared: 'bg-purple-100 text-purple-800',
  archived: 'bg-amber-100 text-amber-800',
};

const aiErrorMessage = (error, fallbackKey, t) =>
  error.response?.data?.message || t(fallbackKey);

const Estimates = () => {
  const { t } = useSettings();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const canManage = [USER_ROLES.ADMIN, USER_ROLES.MANAGER, USER_ROLES.CONTRACTOR].includes(user?.role);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ entity_type: '', status: '' });
  const [aiDisabledNotice, setAiDisabledNotice] = useState(false);

  // Debounce keystrokes so typing does not fire a request (and the list's
  // per-row actuals query) on every character.
  useEffect(() => {
    const handle = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: estimates, isLoading, error } = useQuery({
    queryKey: ['estimates', searchTerm, filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      return api.get(`/estimates?${params.toString()}`).then((res) => res.data);
    },
  });

  const { data: estimateDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['estimate', viewId],
    queryFn: () => api.get(`/estimates/${viewId}`).then((res) => res.data.estimate),
    enabled: Boolean(viewId),
  });

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get('/clients').then((res) => res.data),
    enabled: canManage,
  });

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects').then((res) => res.data),
    enabled: canManage,
  });

  // Admin-only AI settings let the UI explain why AI actions may be unavailable.
  const { data: aiSettings } = useQuery({
    queryKey: ['ai-settings'],
    queryFn: () => api.get('/settings/ai').then((res) => res.data.ai),
    enabled: user?.role === USER_ROLES.ADMIN,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm();

  const watchedAttach = watch('entity_type');
  const selectedClientId = watch('client_id');

  const filteredProjects = selectedClientId
    ? (projects || []).filter((project) => project.client?.id === parseInt(selectedClientId, 10))
    : projects;

  const resetForm = (values = {}) => {
    reset({
      entity_type: 'request',
      ...values,
    });
  };

  // ---- Prefill from inline affordances (project / task / time entry) -------
  useEffect(() => {
    const projectId = searchParams.get('project_id');
    const taskId = searchParams.get('task_id');
    const timeEntryId = searchParams.get('time_entry_id');

    if (!projectId && !taskId && !timeEntryId) return;

    const prefill = async () => {
      const values = { entity_type: 'request' };

      if (taskId && projectId) {
        values.entity_type = 'task';
        values.entity_id = taskId;
        try {
          const res = await api.get(`/projects/${projectId}`);
          const project = res.data.project;
          const task = (res.data.tasks || []).find((item) => String(item.id) === String(taskId));
          values.title = task?.title || task?.name || project?.name || '';
          values.request_text = task?.description || project?.description || '';
        } catch {
          values.request_text = '';
        }
      } else if (projectId) {
        values.entity_type = 'project';
        values.entity_id = projectId;
        try {
          const res = await api.get(`/projects/${projectId}`);
          values.title = res.data.project?.name || '';
          values.request_text = res.data.project?.description || '';
        } catch {
          values.request_text = '';
        }
      } else if (timeEntryId) {
        values.entity_type = 'time_entry';
        values.entity_id = timeEntryId;
        try {
          const res = await api.get(`/time/${timeEntryId}`);
          const entry = res.data.time_entry || res.data.entry || res.data;
          values.request_text = entry?.description || '';
        } catch {
          values.request_text = '';
        }
      }

      resetForm(values);
      setEditing(null);
      setShowFormModal(true);
    };

    prefill();
    // Reset the form whenever the inline link changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/estimates', data),
    onSuccess: () => {
      toast.success(t('estimates.toastCreated'));
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      setShowFormModal(false);
      resetForm();
    },
    onError: (err) => toast.error(aiErrorMessage(err, 'estimates.toastCreateFailed', t)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.put(`/estimates/${id}`, data),
    onSuccess: () => {
      toast.success(t('estimates.toastUpdated'));
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate'] });
      setShowFormModal(false);
      setEditing(null);
      resetForm();
    },
    onError: (err) => toast.error(aiErrorMessage(err, 'estimates.toastUpdateFailed', t)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/estimates/${id}`),
    onSuccess: () => {
      toast.success(t('estimates.toastDeleted'));
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      setViewId(null);
    },
    onError: (err) => toast.error(aiErrorMessage(err, 'estimates.toastDeleteFailed', t)),
  });

  const handleAIError = (err, fallbackKey) => {
    const status = err.response?.status;
    if (status === 503) {
      setAiDisabledNotice(true);
    }
    toast.error(aiErrorMessage(err, fallbackKey, t));
  };

  const estimateMutation = useMutation({
    mutationFn: (id) => api.post(`/estimates/${id}/estimate`),
    onSuccess: () => {
      toast.success(t('estimates.toastEstimateRun'));
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate'] });
    },
    onError: (err) => handleAIError(err, 'estimates.toastEstimateFailed'),
  });

  const valuationMutation = useMutation({
    mutationFn: (id) => api.post(`/estimates/${id}/valuate`, {}),
    onSuccess: () => {
      toast.success(t('estimates.toastValuationRun'));
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate'] });
    },
    onError: (err) => handleAIError(err, 'estimates.toastValuationFailed'),
  });

  const handleSubmitForm = (data) => {
    const payload = sanitizeForm(data, ['client_id', 'project_id', 'entity_id', 'estimated_hours', 'rate']);

    if (!payload.entity_type || payload.entity_type === 'request') {
      delete payload.entity_id;
    }

    if (editing) {
      updateMutation.mutate({ id: editing.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const openCreateModal = () => {
    setEditing(null);
    resetForm();
    setShowFormModal(true);
  };

  const openEditModal = (estimate) => {
    setEditing(estimate);
    resetForm({
      entity_type: estimate.entity_type,
      entity_id: estimate.entity_id || '',
      client_id: estimate.client?.id || '',
      project_id: estimate.project?.id || '',
      title: estimate.title || '',
      request_text: estimate.request_text || '',
      status: estimate.status,
      estimated_hours: estimate.estimated_hours ?? '',
      rate: estimate.rate ?? '',
      currency: estimate.currency || 'USD',
    });
    setShowFormModal(true);
    setViewId(null);
  };

  const handleDelete = (id) => {
    if (window.confirm(t('estimates.confirmDelete'))) {
      deleteMutation.mutate(id);
    }
  };

  const downloadEstimate = async (id) => {
    try {
      const res = await api.get(`/estimates/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `estimate-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(aiErrorMessage(err, 'estimates.downloadError', t));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={t('estimates.loadError')} />;
  }

  const renderStatusBadge = (status) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[status] || STATUS_BADGE.draft}`}>
      {t(STATUS_LABEL_KEYS[status], status)}
    </span>
  );

  const detail = estimateDetail;
  const aiAvailable = aiSettings ? aiSettings.enabled && aiSettings.configured : true;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('estimates.title')}</h1>
          <p className="mt-2 text-gray-600">{t('estimates.subtitle')}</p>
        </div>
        <div className="mt-4 sm:mt-0">
          {canManage && (
            <button onClick={openCreateModal} className="btn-primary btn-md flex items-center">
              <Plus className="w-4 h-4 mr-2" />
              {t('estimates.create')}
            </button>
          )}
        </div>
      </div>

      {aiDisabledNotice && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">{t('estimates.aiDisabledTitle')}</p>
            <p className="text-sm text-amber-700">{t('estimates.aiDisabledHint')}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('estimates.searchPlaceholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>
          <div>
            <select
              value={filters.entity_type}
              onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
              className="input"
            >
              <option value="">{t('estimates.allTypes')}</option>
              {ENTITY_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {t(type.labelKey)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="input"
            >
              <option value="">{t('estimates.allStatuses')}</option>
              {Object.keys(STATUS_LABEL_KEYS).map((status) => (
                <option key={status} value={status}>
                  {t(STATUS_LABEL_KEYS[status])}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{t('estimates.list')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('estimates.titleField')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('estimates.type')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('estimates.estimateLabel')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('estimates.actualLabel')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('estimates.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('estimates.edit')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(estimates || []).map((estimate) => (
                <tr key={estimate.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {estimate.title || `#${estimate.id}`}
                    </div>
                    <div className="text-sm text-gray-500 max-w-md truncate">
                      {estimate.project?.name || estimate.client?.name || ''}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {t(ENTITY_TYPE_LABEL_KEYS[estimate.entity_type], estimate.entity_type)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {estimate.estimated_cost === null
                      ? '—'
                      : formatCurrency(estimate.estimated_cost, estimate.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {estimate.actual ? formatCurrency(estimate.actual.cost, estimate.currency) : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{renderStatusBadge(estimate.status)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setViewId(estimate.id)}
                        className="text-gray-400 hover:text-gray-600"
                        title={t('estimates.view')}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {canManage && (
                        <>
                          <button
                            onClick={() => openEditModal(estimate)}
                            className="text-gray-400 hover:text-primary-600"
                            title={t('estimates.edit')}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => downloadEstimate(estimate.id)}
                            className="text-gray-400 hover:text-gray-600"
                            title={t('estimates.download')}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(estimate.id)}
                            className="text-red-600 hover:text-red-900"
                            title={t('estimates.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(estimates || []).length === 0 && (
          <div className="text-center py-12">
            <Calculator className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('estimates.noEstimates')}</h3>
            <p className="text-gray-600 mb-4">{t('estimates.noEstimatesHint')}</p>
            {canManage && (
              <button onClick={openCreateModal} className="btn-primary btn-md flex items-center mx-auto">
                <Plus className="w-4 h-4 mr-2" />
                {t('estimates.create')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        title={editing ? t('estimates.edit') : t('estimates.createNew')}
        size="lg"
      >
        <form onSubmit={handleSubmit(handleSubmitForm)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('estimates.attachTo')}</label>
              <select {...register('entity_type')} className="input">
                {ENTITY_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {t(type.labelKey)}
                  </option>
                ))}
              </select>
            </div>

            {watchedAttach && watchedAttach !== 'request' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('estimates.entityId')}</label>
                <input
                  {...register('entity_id', { required: t('estimates.entityIdHint') })}
                  type="number"
                  min="1"
                  className={`input ${errors.entity_id ? 'border-red-300' : ''}`}
                />
                <p className="mt-1 text-xs text-gray-500">{t('estimates.entityIdHint')}</p>
              </div>
            )}
          </div>

          {(watchedAttach === undefined || watchedAttach === 'request') && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('estimates.client')}</label>
                <select {...register('client_id')} className="input">
                  <option value="">{t('estimates.selectClient')}</option>
                  {(clients || []).map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('estimates.project')}</label>
                <select {...register('project_id')} className="input">
                  <option value="">{t('estimates.selectProject')}</option>
                  {filteredProjects?.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('estimates.titleField')}</label>
            <input
              {...register('title', { maxLength: 200 })}
              type="text"
              maxLength={200}
              className="input"
              placeholder={t('estimates.titlePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('estimates.requestText')} *</label>
            <textarea
              {...register('request_text', { required: true, maxLength: 20000 })}
              rows={5}
              maxLength={20000}
              className={`input ${errors.request_text ? 'border-red-300' : ''}`}
              placeholder={t('estimates.requestPlaceholder')}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('estimates.estimatedHours')}</label>
              <input {...register('estimated_hours')} type="number" min="0" step="0.01" className="input" />
              <p className="mt-1 text-xs text-gray-500">{t('estimates.manualHoursHint')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('estimates.rate')}</label>
              <input {...register('rate')} type="number" min="0" step="0.01" className="input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('estimates.currency')}</label>
              <input {...register('currency', { maxLength: 3 })} type="text" maxLength={3} className="input" />
            </div>
          </div>

          {editing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('estimates.status')}</label>
              <select {...register('status')} className="input">
                {Object.keys(STATUS_LABEL_KEYS).map((status) => (
                  <option key={status} value={status}>
                    {t(STATUS_LABEL_KEYS[status])}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={() => setShowFormModal(false)} className="btn-outline btn-md">
              {t('estimates.cancel')}
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="btn-primary btn-md flex items-center"
            >
              {createMutation.isPending || updateMutation.isPending ? (
                <LoadingSpinner size="sm" color="white" />
              ) : (
                t('estimates.save')
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        isOpen={Boolean(viewId)}
        onClose={() => setViewId(null)}
        title={t('estimates.details')}
        size="xl"
      >
        {isLoadingDetail ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : detail ? (
          <div className="space-y-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xl font-semibold text-gray-900">{detail.title || `#${detail.id}`}</p>
                <p className="text-sm text-gray-500">
                  {t(ENTITY_TYPE_LABEL_KEYS[detail.entity_type], detail.entity_type)}
                  {detail.project?.name ? ` · ${detail.project.name}` : ''}
                </p>
              </div>
              {renderStatusBadge(detail.status)}
            </div>

            <div>
              <p className="text-sm text-gray-500">{t('estimates.requestText')}</p>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{detail.request_text}</p>
            </div>

            {!aiAvailable && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-700">{t('estimates.aiDisabledHint')}</p>
              </div>
            )}

            {/* Side by side: estimate / actual / valuation */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-gray-200 p-4">
                <p className="text-sm text-gray-500">{t('estimates.estimateLabel')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {detail.estimated_cost === null ? '—' : formatCurrency(detail.estimated_cost, detail.currency)}
                </p>
                <p className="text-xs text-gray-500">
                  {detail.estimated_hours === null ? '' : `${formatNumber(detail.estimated_hours, 2)} ${t('estimates.hoursShort')}`}
                  {detail.rate ? ` · ${formatCurrency(detail.rate, detail.currency)}/${t('estimates.hoursShort')}` : ''}
                </p>
              </div>

              <div className="rounded-lg border border-gray-200 p-4">
                <p className="text-sm text-gray-500">{t('estimates.actualLabel')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {detail.actual ? formatCurrency(detail.actual.cost, detail.currency) : '—'}
                </p>
                <p className="text-xs text-gray-500">
                  {detail.actual ? `${formatNumber(detail.actual.hours, 2)} ${t('estimates.hoursShort')} · ${detail.actual.entries} ${t('estimates.entries')}` : ''}
                </p>
              </div>

              <div className="rounded-lg border border-gray-200 p-4">
                <p className="text-sm text-gray-500 flex items-center">
                  <Sparkles className="w-4 h-4 mr-1 text-primary-600" />
                  {t('estimates.valuationLabel')}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {detail.valuation_cost === null ? '—' : formatCurrency(detail.valuation_cost, detail.currency)}
                </p>
                <p className="text-xs text-gray-500">
                  {detail.valuation_hours === null ? '' : `${formatNumber(detail.valuation_hours, 2)} ${t('estimates.hoursShort')}`}
                </p>
              </div>
            </div>

            {detail.actual && detail.actual.variance_cost !== null && (
              <div className="flex items-center justify-between rounded-lg bg-gray-50 border border-gray-200 p-4 text-sm">
                <span className="text-gray-500">
                  {t('estimates.variance')} <span className="text-xs">({t('estimates.varianceHint')})</span>
                </span>
                <span className={detail.actual.variance_cost > 0 ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>
                  {formatCurrency(detail.actual.variance_cost, detail.currency)}
                </span>
              </div>
            )}

            {detail.project?.billing_type === 'fixed' && detail.client_amount !== null && (
              <div className="flex items-center justify-between rounded-lg bg-primary-50 border border-primary-100 p-4 text-sm">
                <span className="text-primary-700">{t('estimates.clientAmount')} ({t('estimates.fixedPrice')})</span>
                <span className="text-primary-800 font-semibold">{formatCurrency(detail.client_amount, detail.currency)}</span>
              </div>
            )}

            {(detail.ai_rationale || detail.ai_model || detail.ai_confidence) && (
              <div className="text-sm space-y-1">
                <p className="text-gray-500">{t('estimates.aiRationale')}</p>
                {detail.ai_rationale && <p className="text-gray-900 whitespace-pre-wrap">{detail.ai_rationale}</p>}
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  {detail.ai_model && <span>{t('estimates.aiModel')}: {detail.ai_model}</span>}
                  {detail.ai_confidence && <span>{t('estimates.confidence')}: {t(CONFIDENCE_LABEL_KEYS[detail.ai_confidence], detail.ai_confidence)}</span>}
                </div>
              </div>
            )}

            {detail.valuation_rationale && (
              <div className="text-sm space-y-1">
                <p className="text-gray-500">{t('estimates.valuationRationale')}</p>
                <p className="text-gray-900 whitespace-pre-wrap">{detail.valuation_rationale}</p>
              </div>
            )}

            {detail.client && (
              <div className="text-sm">
                <p className="text-gray-500">{t('estimates.client')}</p>
                <p className="font-medium text-gray-900">{detail.client.name}</p>
                {detail.client.company && <p className="text-gray-700">{detail.client.company}</p>}
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-gray-100">
              {canManage && (
                <>
                  <button
                    type="button"
                    onClick={() => estimateMutation.mutate(detail.id)}
                    disabled={estimateMutation.isPending}
                    className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {estimateMutation.isPending ? <LoadingSpinner size="sm" color="white" /> : t('estimates.runEstimate')}
                  </button>
                  <button
                    type="button"
                    onClick={() => valuationMutation.mutate(detail.id)}
                    disabled={valuationMutation.isPending}
                    className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {valuationMutation.isPending ? <LoadingSpinner size="sm" color="white" /> : t('estimates.runValuation')}
                  </button>
                  <button
                    type="button"
                    onClick={() => openEditModal(detail)}
                    className="btn-outline btn-md flex items-center"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    {t('estimates.edit')}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => downloadEstimate(detail.id)}
                className="btn-outline btn-md flex items-center"
              >
                <FileText className="w-4 h-4 mr-2" />
                {t('estimates.download')}
              </button>
              <button type="button" onClick={() => setViewId(null)} className="btn-primary btn-md">
                {t('estimates.close')}
              </button>
            </div>
          </div>
        ) : (
          <div className="py-12 text-center text-sm text-gray-600">{t('estimates.loadError')}</div>
        )}
      </Modal>
    </div>
  );
};

export default Estimates;
